import React from 'react';
import { X } from 'lucide-react';

interface Props {
  onClose: () => void;
}

const HowItWorksModal: React.FC<Props> = ({ onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">How the Compliance Mapping Tool Works</h2>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <X className="w-6 h-6 text-gray-500" />
        </button>
      </div>

      <div className="p-6">
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-blue-700 mb-4">Understanding Cybersecurity Standards Mapping</h3>
          <p className="text-gray-700 mb-4">
            Organizations often need to comply with multiple cybersecurity frameworks and standards simultaneously.
            Our mapping tool helps you identify overlaps and gaps between different frameworks, saving you time and effort
            in implementing controls that satisfy multiple requirements.
          </p>
          <p className="text-gray-700">
            The tool maps requirements across ISO 27001, NIST Cybersecurity Framework, ETSI, and various national frameworks
            from Belgium, Finland, Greece, Spain, and France, allowing you to see how requirements in one framework correspond
            to requirements in others.
          </p>
        </section>

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-blue-700 mb-4">Key Features</h3>

          <div className="mb-6">
            <h4 className="text-lg font-medium text-gray-800 mb-2">1. Multiple View Modes</h4>
            <div className="pl-5 border-l-4 border-blue-100 mb-2">
              <p className="text-gray-700 mb-2"><strong>Grid View:</strong> Default view showing detailed information about each requirement with expandable sections.</p>
              <p className="text-gray-700 mb-2"><strong>Checklist View:</strong> Organize requirements by category with progress tracking.</p>
              <p className="text-gray-700 mb-2"><strong>Visualization View:</strong> Visual representation of your compliance progress and framework coverage.</p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-medium text-gray-800 mb-2">2. Search and Filtering</h4>
            <div className="pl-5 border-l-4 border-blue-100 mb-2">
              <p className="text-gray-700 mb-2">Search across all frameworks by ID, title, or any reference number.</p>
              <p className="text-gray-700 mb-2">Filter by category to focus on specific domains like Risk Management, Access Control, etc.</p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-medium text-gray-800 mb-2">3. Compliance Tracking</h4>
            <div className="pl-5 border-l-4 border-blue-100 mb-2">
              <p className="text-gray-700 mb-2">Mark requirements as "Not Started," "In Progress," or "Completed" to track your compliance journey.</p>
              <p className="text-gray-700 mb-2">View overall completion rates and progress by category.</p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg font-medium text-gray-800 mb-2">4. Requirement Levels</h4>
            <div className="pl-5 border-l-4 border-blue-100 mb-2">
              <p className="text-gray-700 mb-2">Requirements are categorized by importance level:</p>
              <div className="flex gap-2 mb-2">
                <span className="cs-badge cs-badge-green">BASIC</span>
                <span className="text-gray-700">Fundamental controls that form the foundation of your security program.</span>
              </div>
              <div className="flex gap-2 mb-2">
                <span className="cs-badge cs-badge-yellow">IMPORTANT</span>
                <span className="text-gray-700">Significant controls that enhance your security posture.</span>
              </div>
              <div className="flex gap-2">
                <span className="cs-badge cs-badge-red">ESSENTIAL</span>
                <span className="text-gray-700">Critical controls that require immediate attention.</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-medium text-gray-800 mb-2">5. Export Capabilities</h4>
            <div className="pl-5 border-l-4 border-blue-100">
              <p className="text-gray-700 mb-2">Export your filtered results as CSV or JSON for further analysis or reporting.</p>
              <p className="text-gray-700">Share your compliance status with stakeholders or integrate with other tools.</p>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h3 className="text-xl font-semibold text-blue-700 mb-4">How to Use the Tool</h3>
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-800 mb-2">Step 1: Explore Requirements</h4>
              <p className="text-gray-700">Browse through the requirements or use the search function to find specific controls. Click on any requirement to expand and see detailed mapping information.</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-800 mb-2">Step 2: Track Your Progress</h4>
              <p className="text-gray-700">Use the dropdown menu for each requirement to mark its implementation status. Your progress is automatically saved in your browser.</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-800 mb-2">Step 3: View Detailed Information</h4>
              <p className="text-gray-700">Click "View Full Details" to see comprehensive mapping information for any requirement, including all cross-references.</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-800 mb-2">Step 4: Export Your Results</h4>
              <p className="text-gray-700">Use the Export or Download buttons to save your filtered results for offline use or reporting.</p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-blue-700 mb-4">Benefits</h3>
          <ul className="list-disc pl-5 space-y-2 text-gray-700">
            <li>Reduce duplication of effort by implementing controls that satisfy multiple frameworks</li>
            <li>Identify gaps in your compliance program across different frameworks</li>
            <li>Prioritize your security efforts based on requirement levels</li>
            <li>Track your compliance progress over time</li>
            <li>Generate reports for stakeholders and auditors</li>
          </ul>
        </section>
      </div>

      <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 flex justify-end">
        <button
          onClick={onClose}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Got it
        </button>
      </div>
    </div>
  </div>
);

export default HowItWorksModal;
